import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { getIntegrationQueryKeys } from '../../integrationUtils';
import {
  IMPORT_STATE,
  type ImportHistoryItemT,
  type ImportRuleT,
  type ZendeskInfo,
} from '../types';

const DEFAULT_IMPORT_RULE: ImportRuleT = {
  status: 1,
  bound: 1,
  tag: 1,
  tag_names: [],
};

const mapZendeskInfo = (data: unknown): ZendeskInfo | null => {
  if (!data || typeof data !== 'object') return null;

  const info = data as {
    workspace_id?: string;
    alias?: string;
    domain?: string;
    import_state?: IMPORT_STATE;
    history?: ImportHistoryItemT[];
    import_rule?: ImportRuleT;
  };

  if (!info.alias) return null;

  return {
    workspace_id: info.workspace_id || info.alias,
    alias: info.alias,
    domain: info.domain || '',
    import_state: info.import_state || IMPORT_STATE.NOT_STARTED,
    history: info.history || [],
    import_rule: info.import_rule || DEFAULT_IMPORT_RULE,
  };
};

export const useZendesk = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspaceAlias = currWorkspace?.alias;

  if (!workspaceAlias) throw new Error('Workspace alias is required');

  const queryKey = getIntegrationQueryKeys(SOURCE_TYPE.ZENDESK, workspaceAlias);

  const { data: zendeskWorkspace } = useQuery({
    queryKey,
    queryFn: async () =>
      mapZendeskInfo(await service.zendesk.getZendeskInfo(workspaceAlias)),
    enabled,
  });

  const setZendeskWorkspace = (workspace: unknown) => {
    const mapped = mapZendeskInfo(workspace);
    if (!mapped) return;

    queryClient.setQueryData<ZendeskInfo | null>(queryKey, mapped);
  };

  const zendeskAlias = zendeskWorkspace?.alias ?? '';

  const { mutate: disconnectZendeskMutation } = useMutation({
    mutationFn: () => service.zendesk.deleteZendeskInfo(zendeskAlias),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const { mutateAsync: updateZendeskImportRuleMutation } = useMutation({
    mutationFn: (importRule: ImportRuleT) =>
      service.zendesk.updateZendeskImportOption(importRule),
    onSuccess: (_, importRule) => {
      queryClient.setQueryData<ZendeskInfo | null>(queryKey, (current) => {
        if (!current) return current;
        return {
          ...current,
          import_rule: importRule,
        };
      });
    },
  });

  const { mutateAsync: requestImportMutation } = useMutation({
    mutationFn: async ({
      start_date,
      end_date,
      importRule,
    }: {
      start_date: string;
      end_date: string;
      importRule: ImportRuleT;
    }) => {
      await service.zendesk.updateZendeskImportOption(importRule);
      return service.zendesk.importWorkspaceFeedback(
        workspaceAlias,
        start_date,
        end_date,
      );
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData<ZendeskInfo | null>(queryKey, (current) => {
        if (!current) return current;
        return {
          ...current,
          import_rule: variables.importRule,
          import_state: IMPORT_STATE.IMPORTING,
        };
      });
    },
  });

  return {
    zendeskWorkspace,
    disconnectZendeskMutation,
    updateZendeskImportRuleMutation,
    requestImportMutation,
    setZendeskWorkspace,
    invalidateZendeskQuery: () => queryClient.invalidateQueries({ queryKey }),
  };
};
